import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
	/** Current page (1-based). */
	page: number;
	/** Items per page. */
	pageSize: number;
	/** Total number of items across all pages. */
	totalCount: number;
	/** Called with the new page number when the user navigates. */
	onPageChange: (page: number) => void;
	/** Word used in the summary — e.g. "items", "users", "hashtags". Defaults to "items". */
	itemLabel?: string;
	/** Hide the control when only one page exists. Defaults to true. */
	hideWhenSinglePage?: boolean;
}

const Pagination = ({
	page,
	pageSize,
	totalCount,
	onPageChange,
	itemLabel = "items",
	hideWhenSinglePage = true
}: PaginationProps) => {
	const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
	const safePage = Math.min(Math.max(1, page), totalPages);

	if (hideWhenSinglePage && totalPages <= 1) return null;

	const from = totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
	const to = Math.min(safePage * pageSize, totalCount);

	return (
		<nav
			aria-label="Pagination"
			className="flex items-center justify-between mt-4 text-sm"
		>
			<p className="text-muted-foreground">
				Showing {from} to {to} of {totalCount} {itemLabel}
			</p>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={() => onPageChange(Math.max(1, safePage - 1))}
					disabled={safePage <= 1}
					className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-muted disabled:opacity-40 disabled:pointer-events-none transition-colors"
				>
					<ChevronLeft className="h-4 w-4" />
					Previous
				</button>
				<span className="text-muted-foreground px-2">
					{safePage} of {totalPages}
				</span>
				<button
					type="button"
					onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
					disabled={safePage >= totalPages}
					className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-muted disabled:opacity-40 disabled:pointer-events-none transition-colors"
				>
					Next
					<ChevronRight className="h-4 w-4" />
				</button>
			</div>
		</nav>
	);
};

export default Pagination;
